import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, CalendarDays, Bell, Users, X, Search, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ShareByEmailDialog from "@/components/ShareByEmailDialog";
import { useRowHighlight } from "@/lib/useRowHighlight";

const TIPOS = ["nota", "cita", "recordatorio", "tarea", "alerta"];
const PRIORIDADES = ["baja", "normal", "alta"];

export default function CalendarPage() {
  const { user } = useAuth();
  const { highlightId, setRef, flash } = useRowHighlight();
  const [events, setEvents] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState(emptyForm());
  // Compartir por email
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEvent, setShareEvent] = useState(null);
  const abrirCompartir = (ev) => {
    setShareEvent(ev);
    setShareOpen(true);
  };
  function emptyForm() {
    return {
      titulo: "", descripcion: "",
      fecha_inicio: new Date().toISOString().slice(0,16), fecha_fin: "",
      tipo: "nota", prioridad: "normal", completado: false,
      participantes: [], recordatorio_minutos: 0,
    };
  }

  const load = async () => {
    try {
      const [evs, us] = await Promise.all([
        api.get("/calendar/eventos"),
        api.get("/calendar/usuarios"),
      ]);
      setEvents(evs.data);
      setUsuarios(us.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (!form.titulo.trim()) return toast.error("El título es obligatorio");
      const payload = {...form};
      if (!payload.fecha_fin) delete payload.fecha_fin;
      let savedId = null;
      if (editing) {
        const res = await api.patch(`/calendar/eventos/${editing.id}`, payload);
        savedId = res?.data?.id || editing.id;
      } else {
        const res = await api.post("/calendar/eventos", payload);
        savedId = res?.data?.id;
      }
      toast.success("Evento guardado"); setDialogOpen(false);
      await load();
      if (savedId) flash(savedId);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.titulo}"?`)) return;
    try { await api.delete(`/calendar/eventos/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleComplete = async (it) => {
    try { await api.patch(`/calendar/eventos/${it.id}`, { ...it, completado: !it.completado }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const eventsByDate = useMemo(() => {
    const m = {};
    events.forEach(ev => {
      const d = ev.fecha_inicio?.slice(0,10);
      if (!m[d]) m[d] = [];
      m[d].push(ev);
    });
    return m;
  }, [events]);

  const selectedISO = selectedDate ? selectedDate.toISOString().slice(0,10) : null;
  const dayEvents = selectedISO ? (eventsByDate[selectedISO] || []) : [];
  const markedDates = Object.keys(eventsByDate).map(d => new Date(d + "T12:00:00"));

  const usuariosMap = useMemo(() => {
    const m = {};
    usuarios.forEach(u => { m[u.id] = u; });
    return m;
  }, [usuarios]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Personal</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Calendario</h1>
          <p className="text-sm text-[#4a5c53] mt-1">
            Notas, citas, tareas y recordatorios — comparta eventos con compañeros añadiéndolos como participantes.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({...emptyForm(), fecha_inicio: (selectedDate || new Date()).toISOString().slice(0,16)}); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-event-btn">
          <Plus size={16} className="mr-2" /> Nuevo evento
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#d1dbd6] rounded-md p-3">
          <Calendar
            mode="single"
            locale={es}
            selected={selectedDate}
            onSelect={setSelectedDate}
            modifiers={{ hasEvent: markedDates }}
            modifiersStyles={{ hasEvent: { fontWeight: "700", color: "#205c45", backgroundColor: "#e9f2ee" } }}
            className="rounded-md"
          />
          <div className="text-xs text-[#5c7368] mt-3 border-t border-[#d1dbd6] pt-3 space-y-1">
            <div><Bell size={12} className="inline mr-1" /> Los días marcados tienen eventos.</div>
            <div><Users size={12} className="inline mr-1" /> Los eventos con participantes son compartidos.</div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <CalendarDays className="inline mr-2" size={18} />
              {selectedDate?.toLocaleDateString("es-EC", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </h2>
            <div className="text-sm text-[#5c7368]">{dayEvents.length} evento(s)</div>
          </div>

          <div className="space-y-2">
            {dayEvents.map(ev => (
              <div key={ev.id} ref={setRef(ev.id)} className={`bg-white border rounded-md p-4 flex items-start gap-3 ${ev.completado ? "opacity-60" : ""} ${!ev.es_creador ? "border-l-4 border-l-[#205c45]" : ""} ${highlightId === ev.id ? "row-highlight" : ""}`} data-testid={`event-${ev.id}`}>
                <Checkbox checked={ev.completado} onCheckedChange={() => toggleComplete(ev)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`font-semibold ${ev.completado ? "line-through" : ""}`}>{ev.titulo}</span>
                    <Badge variant="outline" className="capitalize text-[10px]">{ev.tipo}</Badge>
                    {ev.prioridad === "alta" && <Badge className="fefo-vencido border text-[10px]">Alta</Badge>}
                    {!ev.es_creador && (
                      <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] text-[10px]">
                        <Users size={10} className="inline mr-1" /> Invitado por {ev.user_nombre || "—"}
                      </Badge>
                    )}
                  </div>
                  {ev.descripcion && <div className="text-sm text-[#4a5c53]">{ev.descripcion}</div>}
                  <div className="text-xs text-[#8ba397] mt-1">
                    {ev.fecha_inicio?.replace("T"," ").slice(0,16)}
                    {ev.fecha_fin && <> → {ev.fecha_fin?.replace("T"," ").slice(0,16)}</>}
                  </div>
                  {ev.participantes_nombres && ev.participantes_nombres.length > 0 && (
                    <div className="mt-2 flex gap-1 flex-wrap" data-testid={`participantes-${ev.id}`}>
                      <span className="text-[10px] uppercase tracking-wider text-[#5c7368] mr-1">Participantes:</span>
                      {ev.participantes_nombres.map(p => (
                        <Badge key={p.id} variant="outline" className="text-[10px] bg-white">
                          {p.nombre}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => abrirCompartir(ev)} title="Compartir por correo (PDF)" data-testid={`event-share-${ev.id}`}><Mail size={14} className="text-[#205c45]" /></Button>
                  {ev.es_creador && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => {
                        setEditing(ev);
                        setForm({
                          ...emptyForm(),
                          ...ev,
                          fecha_inicio: ev.fecha_inicio?.slice(0,16),
                          recordatorio_minutos: ev.recordatorio_minutos ?? 0,
                          fecha_fin: ev.fecha_fin?.slice(0,16) || "",
                          participantes: ev.participantes || [],
                        });
                        setDialogOpen(true);
                      }} data-testid={`edit-event-${ev.id}`}><Edit2 size={14} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(ev)}><Trash2 size={14} className="text-[#b53a25]" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {dayEvents.length === 0 && (
              <div className="bg-white border border-dashed border-[#d1dbd6] rounded-md p-10 text-center text-[#8ba397]">
                Sin eventos para este día.
              </div>
            )}
          </div>
        </div>
      </div>

      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#d1dbd6]">
            <SheetTitle>{editing ? "Editar evento" : "Nuevo evento"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Título</Label>
              <Input value={form.titulo} onChange={(e) => setForm({...form, titulo: e.target.value})} data-testid="event-title-input" placeholder="Ej: Reunión con Mario Estrella" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                <SelectTrigger data-testid="event-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORIDADES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Inicio</Label><Input type="datetime-local" value={form.fecha_inicio} onChange={(e) => setForm({...form, fecha_inicio: e.target.value})} /></div>
            <div><Label>Fin (opcional)</Label><Input type="datetime-local" value={form.fecha_fin} onChange={(e) => setForm({...form, fecha_fin: e.target.value})} /></div>

            {/* Participantes */}
            <div className="col-span-2">
              <Label className="flex items-center gap-1">
                <Users size={14} /> Compartir con compañeros (opcional)
              </Label>
              <ParticipantesPicker
                usuarios={usuarios}
                value={form.participantes}
                onChange={(arr) => setForm({...form, participantes: arr})}
              />
              {form.participantes.length > 0 && (
                <div className="mt-2 flex gap-1 flex-wrap">
                  {form.participantes.map(pid => (
                    <Badge key={pid} className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] flex items-center gap-1" data-testid={`chip-${pid}`}>
                      {usuariosMap[pid]?.nombres} {usuariosMap[pid]?.apellidos}
                      <button
                        type="button"
                        onClick={() => setForm({...form, participantes: form.participantes.filter(x => x !== pid)})}
                        className="hover:text-[#b53a25]"
                      >
                        <X size={11} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[#5c7368] mt-1">
                El evento aparecerá también en el calendario de las personas seleccionadas. Solo tú podrás editarlo, ellos pueden marcarlo como completado.
              </p>
            </div>

            {/* ITER208: alerta programada */}
            <div className="col-span-2">
              <Label className="flex items-center gap-1"><Bell size={14} /> Alerta emergente</Label>
              <Select value={String(form.recordatorio_minutos ?? 0)}
                onValueChange={(v) => setForm({...form, recordatorio_minutos: parseInt(v, 10)})}>
                <SelectTrigger data-testid="event-recordatorio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sin alerta</SelectItem>
                  <SelectItem value="5">5 minutos antes</SelectItem>
                  <SelectItem value="15">15 minutos antes</SelectItem>
                  <SelectItem value="30">30 minutos antes</SelectItem>
                  <SelectItem value="60">1 hora antes</SelectItem>
                  <SelectItem value="180">3 horas antes</SelectItem>
                  <SelectItem value="1440">1 día antes</SelectItem>
                  <SelectItem value="2880">2 días antes</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#5c7368] mt-1">
                Recibirás una notificación emergente (y push si están activadas) a ti y a los participantes.
              </p>
            </div>
            <div className="col-span-2"><Label>Descripción</Label><Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} /></div>
            <div className="col-span-2 flex items-center gap-2"><Checkbox checked={form.completado} onCheckedChange={(v) => setForm({...form, completado: !!v})} /> <Label>Marcado como completado</Label></div>
            </div>
          </div>
          <SheetFooter className="border-t border-[#d1dbd6] px-6 py-4 gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-event-btn">Guardar</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ShareByEmailDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        tipo="evento"
        item={shareEvent}
        endpoint="/calendar/eventos/{id}/enviar-pdf"
      />
    </div>
  );
}

/** Selector múltiple de usuarios con búsqueda. */
function ParticipantesPicker({ usuarios, value, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter(x => x !== id));
    else onChange([...value, id]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full h-10 justify-start font-normal text-[#5c7368]" data-testid="participantes-trigger">
          <Users size={14} className="mr-2" />
          {value.length === 0 ? "Buscar y agregar personas…" : `${value.length} persona(s) seleccionada(s)`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(420px,calc(100vw-24px))] max-w-[420px] p-0" align="start">
        <Command shouldFilter={true} filter={(v, s) => v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0}>
          <div className="flex items-center border-b border-[#d1dbd6] px-3">
            <Search size={14} className="mr-2 shrink-0 opacity-50" />
            <CommandInput placeholder="Buscar por nombre o correo…" className="flex h-10 w-full bg-transparent py-3 text-sm outline-none" data-testid="participantes-input" />
          </div>
          <CommandList className="max-h-[280px] overflow-y-auto">
            <CommandEmpty className="py-4 text-center text-sm text-[#8ba397]">Sin usuarios</CommandEmpty>
            <CommandGroup>
              {usuarios.map(u => {
                const searchable = `${u.nombres} ${u.apellidos} ${u.email}`;
                const selected = value.includes(u.id);
                return (
                  <CommandItem
                    key={u.id}
                    value={searchable}
                    onSelect={() => toggle(u.id)}
                    data-testid={`participante-item-${u.id}`}
                  >
                    <Checkbox checked={selected} className="mr-2" />
                    <div className="flex flex-col">
                      <span className="font-medium">{u.nombres} {u.apellidos}</span>
                      <span className="text-[11px] text-[#5c7368] capitalize">{u.role} · {u.email}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
